import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { Header, Footer, ScrollToTop } from './components/Header';
import svgPaths from "../../assets/new_ui_imports/Home/svg-tl21z7gm4q.js";
import { imgGroup, imgVector, imgGroup1 } from "../../assets/new_ui_imports/Home/svg-q9y98.js";
import heroImage from "../../assets/new_ui_img/hero_image.png";
import howitworksImage from "../../assets/new_ui_img/how_it_works_illustration.svg";
import entryImage from "../../assets/new_ui_img/entry_image.jpg";
import sysreqImage from "../../assets/new_ui_img/system_requirement_img.png";

// ─── Scroll reveal hook ──────────────────────────────────────────────────────
function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" }
    );
    document.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// ─── Parallax hook ──────────────────────────────────────────────────────────
function useParallax(ref, factor = 0.35) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handle = () => {
      const rect = el.getBoundingClientRect();
      const offset = (window.innerHeight / 2 - rect.top - rect.height / 2) * factor;
      const bg = el.querySelector(".parallax-bg");
      if (bg) bg.style.transform = `translateY(${offset}px)`;
    };
    window.addEventListener("scroll", handle, { passive: true });
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, [ref, factor]);
}

// ─── Car illustration ─────────────────────────────────────────────────────────
function CarIllustration() {
  return (
    <div className="flex-none rotate-180">
      <div className="h-[360px] w-[180px] sm:h-[479px] sm:w-[239px] relative" data-name="Layer_1">
        <div
          className="absolute mask-alpha mask-intersect mask-no-clip mask-no-repeat"
          style={{
            inset: "calc(9.17%) calc(21.6%) calc(83.6%) calc(19.62%)",
            maskImage: `url("${imgGroup}")`,
            maskPosition: "-1.665px -348.57px",
            maskSize: "145.072px 381.948px",
          }}
        >
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 140.478 34.6331" preserveAspectRatio="none">
            <path d={svgPaths.p24271a80} fill="black" />
            <path d={svgPaths.p3b13b00} fill="#F20000" />
            <path d={svgPaths.p34000ac0} fill="#F20000" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(9.43%) calc(20.91%) calc(10.83%) calc(18.39%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 145.072 381.948" preserveAspectRatio="none">
            <path d={svgPaths.p134ce200} fill="white" />
          </svg>
        </div>
        <div
          className="absolute mask-intersect mask-no-clip mask-no-repeat"
          style={{
            inset: "calc(44.98%) calc(48.38%) calc(52.33%) calc(46.39%)",
            maskImage: `url("${imgVector}"), url("${imgGroup1}")`,
            maskPosition: "-53.039px -24.965px, -9.226px -12.871px",
            maskSize: "121.26px 57.188px, 32.514px 40.273px",
          }}
        >
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 12.4869 12.8867" preserveAspectRatio="none">
            <path d={svgPaths.p5e69000} fill="#878787" />
          </svg>
        </div>
        <div
          className="absolute mask-alpha mask-intersect mask-no-clip mask-no-repeat"
          style={{
            inset: "calc(41.95%) calc(44.52%) calc(49.64%) calc(41.88%)",
            maskImage: `url("${imgVector}")`,
            maskPosition: "-43.814px -12.096px",
            maskSize: "121.26px 57.188px",
          }}
        >
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 32.5128 40.2711" preserveAspectRatio="none">
            <path d={svgPaths.p5fda600} fill="#515151" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(40.95%) calc(26.19%) calc(47.11%) calc(23.08%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 121.26 57.1849" preserveAspectRatio="none">
            <path d={svgPaths.p936cdf0} fill="white" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(56.92%) calc(27.16%) calc(28.62%) calc(24.52%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 115.504 69.2662" preserveAspectRatio="none">
            <path d={svgPaths.pfea2e80} fill="black" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(59.89%) calc(16.05%) calc(36.75%) calc(76.76%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 17.1695 16.0664" preserveAspectRatio="none">
            <path d={svgPaths.pc7da280} fill="black" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(59.89%) calc(79.4%) calc(36.75%) calc(13.41%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 17.1695 16.0664" preserveAspectRatio="none">
            <path d={svgPaths.p2a4b7380} fill="black" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(20.1%) calc(74.5%) calc(33.49%) calc(21.32%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 9.98184 222.296" preserveAspectRatio="none">
            <path d={svgPaths.p1a9bc700} fill="black" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(20.1%) calc(24.22%) calc(33.49%) calc(71.6%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 9.98184 222.296" preserveAspectRatio="none">
            <path d={svgPaths.p12179400} fill="black" />
          </svg>
        </div>
        <div className="absolute" style={{ inset: "calc(22.45%) calc(50.2%) calc(74.19%) calc(48.22%)" }}>
          <svg className="absolute block inset-0 size-full" fill="none" viewBox="0 0 3.7773 16.1084" preserveAspectRatio="none">
            <path d={svgPaths.p2f964380} fill="#C6C6C6" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ─── Hero Section ─────────────────────────────────────────────────────────────
function HeroSection() {
  const sectionRef = useRef(null);
  useParallax(sectionRef, 0.3);

  return (
    <section ref={sectionRef} className="relative w-full h-screen min-h-[600px] max-h-[940px] overflow-hidden">
      <div className="parallax-bg absolute inset-0 will-change-transform hero-bg-animate"
        style={{ top: "-10%", height: "120%", transformOrigin: "center top" }}
      >
        <img src={heroImage} alt="Aerial view of parking lot" className="w-full h-full object-cover" />
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-[rgba(0,0,0,0.65)] via-[30%] to-transparent" />

      <div className="relative z-10 flex flex-col items-center h-full pt-36 px-1">
        <div className="text-center max-w-6xl mx-auto hero-title-animate">
          <h1 className="font-['Montserrat:Bold',sans-serif] font-bold text-white leading-[1.3] text-[clamp(2.2rem,6vw,4.5rem)] whitespace-pre-wrap">
            Maximize your
            <br />
            Underutilized Parking Spaces
          </h1>
          <p className="mt-4 font-['Lato:SemiBold',sans-serif] text-[#BBE8B1] text-[clamp(1.1rem,2.5vw,2.25rem)] leading-[1.4]">
            Turn every parking lot into a revenue-generating mobility hub
          </p>
        </div>

        <div className="mt-auto mb-8 lg:mb-12 bg-[rgba(0,0,0,0.8)] rounded-2xl border border-[#333] px-4 py-4 w-full max-w-[1200px] flex flex-col lg:flex-row gap-6 items-start lg:items-center reveal delay-200">
          <p className="font-['Lato:Regular',sans-serif] text-white text-[clamp(0.95rem,1.8vw,1.25rem)] leading-[1.3] flex-1">
            An intelligent parking platform connecting parking owners, operators, and autonomous fleets through real-time availability and optimized utilization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
            <button className="bg-transparent border border-white text-white font-['Lato:Regular',sans-serif] text-[1.1rem] px-5 py-3 rounded-[5px] hover:bg-white/10 transition-colors whitespace-nowrap">
              View Features
            </button>
            <NavLink to="/signup" className="bg-white text-[#0a66c2] font-['Lato:Regular',sans-serif] text-[1.1rem] px-5 py-3 rounded-[5px] hover:bg-gray-100 transition-colors whitespace-nowrap flex items-center gap-3">
              Get Started Here
              <svg className="w-5 shrink-0" viewBox="0 0 20 14.73" fill="none">
                <path d={svgPaths.pd274400} fill="#0A66C2" />
              </svg>
            </NavLink>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── How It Works Section ─────────────────────────────────────────────────────
function HowItWorksSection() {
  useReveal();

  return (
    <section className="bg-[#f5f5f5] py-16 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="reveal mb-12 lg:mb-16">
          <h2 className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium text-black text-[clamp(2rem,4vw,3.25rem)] leading-[1.3]"
            style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
            How it Works
          </h2>
          <p className="mt-3 font-['Lato:Regular',sans-serif] text-[#6b6b6b] text-[clamp(1rem,1.6vw,1.25rem)] leading-[1.3]">
            In just a few steps, your parking lot becomes part of the autonomous mobility ecosystem.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          <div className="reveal-left bg-[#ebebeb] rounded-[10px] overflow-hidden flex items-center justify-center min-h-[320px] lg:min-h-[480px]">
            {/*<CarIllustration />*/}
            <img src={howitworksImage} alt="Car model" />
          </div>

          <div className="reveal-right flex flex-col gap-10 justify-center py-4 lg:py-8 lg:pl-6">
            <div className="delay-100">
              <h3 className="font-['Google_Sans_Flex:SemiBold',sans-serif] font-semibold text-black text-[clamp(1.1rem,1.8vw,1.5rem)] leading-[1.3] mb-3"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                Step 1 : Sign Up &amp; Configure
              </h3>
              <ul className="list-disc pl-6 font-['Lato:Regular',sans-serif] text-[#616161] text-[clamp(0.9rem,1.2vw,1rem)] space-y-2 leading-[1.3]">
                <li>Create your free account and set designated spaces.</li>
                <li>Choose when and where Robotaxis can park — on your terms.</li>
              </ul>
            </div>

            <div className="delay-200">
              <h3 className="font-['Google_Sans_Flex:SemiBold',sans-serif] font-semibold text-black text-[clamp(1.1rem,1.8vw,1.5rem)] leading-[1.3] mb-3"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                Step 2 : We Connect Your Property
              </h3>
              <ul className="list-disc pl-6 font-['Lato:Regular',sans-serif] text-[#616161] text-[clamp(0.9rem,1.2vw,1rem)] space-y-2 leading-[1.3]">
                <li>GeoParking verifies and publishes your parking inventory.</li>
                <li>Your property becomes available to participating autonomous fleet operators through our Fleet API.</li>
              </ul>
            </div>

            <div className="delay-300">
              <h3 className="font-['Google_Sans_Flex:SemiBold',sans-serif] font-semibold text-black text-[clamp(1.1rem,1.8vw,1.5rem)] leading-[1.3] mb-3"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                Step 3 : Earn &amp; Drive Sustainability
              </h3>
              <ul className="list-disc pl-6 font-['Lato:Regular',sans-serif] text-[#616161] text-[clamp(0.9rem,1.2vw,1rem)] leading-[1.3]">
                <li>Your property becomes a zero-carbon transportation mode — benefiting both the environment and your community.</li>
                <li>Earning on unused parking.</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 delay-400">
              <NavLink to="/signup" className="bg-[#0a66c2] text-white font-['Lato:Regular',sans-serif] text-[1.1rem] h-[50px] px-6 rounded-[5px] flex items-center gap-3 hover:bg-[#0856a8] transition-colors">
                Sign Up Today
                <svg className="w-5 shrink-0" viewBox="0 0 20 14.73" fill="none">
                  <path d={svgPaths.pd274400} fill="white" />
                </svg>
              </NavLink>
              <div className="flex items-center gap-2">
                <svg className="w-6 h-6 shrink-0" viewBox="0 0 24 24" fill="none">
                  <path d={svgPaths.pace200} stroke="#707070" strokeWidth="1.5" />
                  <path d="M12 8V12L14 14" stroke="#707070" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                </svg>
                <span className="font-['Lato:Regular',sans-serif] text-[#616161] text-[1rem]">8-minute Onboarding to complete steps</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Image Showcase Section ───────────────────────────────────────────────────
function ImageShowcaseSection() {
  const sectionRef = useRef(null);
  useParallax(sectionRef, 0.25);

  const images = [
    /*"https://images.unsplash.com/photo-1543465077-db45d34b88a5?w=1920&h=800&fit=crop&auto=format",
    "https://images.unsplash.com/photo-1697761221129-fdf528507890?w=1920&h=800&fit=crop&auto=format",
    "https://images.unsplash.com/photo-1536585422010-b58dd25bb946?w=1920&h=800&fit=crop&auto=format",
    "https://images.unsplash.com/photo-1616363088386-31c4a8414858?w=1920&h=800&fit=crop&auto=format",*/    
  ];

  <img src={entryImage} alt="Aerial view of parking lot" className="w-full h-full object-cover" />

  return (
    <section ref={sectionRef} className="relative w-full h-[400px] md:h-[560px] lg:h-[700px] overflow-hidden">
      <div className="parallax-bg absolute inset-0 will-change-transform" style={{ top: "-8%", height: "116%" }}>
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: i === images.length - 1 ? 1 : 0, zIndex: i }}
          />
        ))}
        <img
          src={entryImage}
          alt="Parking lot aerial view"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 5 }}
        />
      </div>
      <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/20 via-transparent to-black/30 pointer-events-none" />
    </section>
  );
}

// ─── System Requirements Section ─────────────────────────────────────────────
function SystemRequirementsSection() {
  const sectionRef = useRef(null);
  useParallax(sectionRef, 0.2);

  return (
    <section ref={sectionRef} className="relative w-full overflow-hidden ">
      <div className="absolute left-0 top-0 bottom-0 w-full lg:w-[55%] overflow-hidden">
        <div className="parallax-bg absolute inset-0 will-change-transform" style={{ top: "-17%", height: "100%" }}>
          <img src={sysreqImage} alt="Parking management system" className="w-full h-full"/>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[rgba(245,245,245,0.1)] via-[rgba(245,245,245,0.5)] to-[#f5f5f5]" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 py-16 lg:py-24 flex justify-end">
        <div className="w-full lg:w-[60%]">
          <div className="reveal">
            <h2 className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium text-black text-[clamp(1.8rem,3.5vw,3.25rem)] leading-[1.3] text-right"
              style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
              System Requirements
            </h2>
            <p className="mt-3 font-['Lato:Regular',sans-serif] text-[#333] text-[clamp(0.95rem,1.5vw,1.25rem)] leading-[1.3] text-right">
              GeoParking is optimized for modern desktop browsers to deliver the best performance and reliability. Please review the recommended system requirements below before accessing the platform.
            </p>
          </div>

          <div className="reveal delay-100 mt-5 bg-[#e2f0fd] px-5 py-3 rounded-[5px] text-right">
            <p className="font-['Lato:Regular',sans-serif] text-[#0a66c2] text-[clamp(0.85rem,1.2vw,1.1rem)] leading-[1.5]">
              Platform compatibility will continue to expand through future GeoParking software releases.
            </p>
          </div>

          <div className="reveal delay-200 mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-10 h-10 shrink-0" viewBox="0 0 40 40" fill="none">
                  <path d={svgPaths.p2c478b00} stroke="#388E3C" strokeLinecap="round" strokeWidth="2.5" />
                  <path d="M20 30V36.6667" stroke="#388E3C" strokeWidth="2.5" />
                  <path d="M13.3333 36.6667H26.6667" stroke="#388E3C" strokeLinecap="round" strokeWidth="2.5" />
                  <path d="M18.3333 25H21.6667" stroke="#388E3C" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d={svgPaths.p226e4f70} stroke="#388E3C" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                </svg>
                <h3 className="font-['Lato:Bold',sans-serif] font-bold text-[#121212] text-[1.25rem] leading-[1.3]">Supported Platforms</h3>
              </div>
              <ul className="list-disc pl-5 font-['Google_Sans_Flex:Regular',sans-serif] text-[#686868] text-[clamp(0.9rem,1.1vw,1.1rem)] space-y-2 leading-[1.3]"
                style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                <li>
                  <span className="font-['Lato:Medium',sans-serif] text-[#121212]">Operating Systems:</span>
                  <span className="font-['Lato:Regular',sans-serif]"> Windows 11, macOS Sequoia</span>
                </li>
                <li>
                  <span className="font-['Lato:Medium',sans-serif] text-[#121212]">Browser:</span>
                  <span className="font-['Lato:Regular',sans-serif]"> Chrome, Edge, Safari</span>
                </li>
                <li>
                  <span className="font-['Lato:Medium',sans-serif] text-[#121212]">Hardware:</span>
                  <span className="font-['Lato:Regular',sans-serif]"> 8 GB RAM minimum, Modern dual-core processor, Broadband internet (10 Mbps+)</span>
                </li>
              </ul>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-10 h-10 shrink-0" viewBox="0 0 40 40" fill="none">
                  <path d={svgPaths.p1fa9c000} stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d={svgPaths.pf28fa00} stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d={svgPaths.p17921780} stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                  <path d={svgPaths.p2b7c1c80} stroke="#0A66C2" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" />
                </svg>
                <h3 className="font-['Google_Sans_Flex:SemiBold',sans-serif] font-semibold text-[#121212] text-[1.25rem] leading-[1.3]"
                  style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
                  Usage Limitations
                </h3>
              </div>
              <ul className="list-disc pl-5 font-['Lato:Regular',sans-serif] text-[#686868] text-[clamp(0.9rem,1.1vw,1.1rem)] space-y-2 leading-[1.3]">
                <li>Mobile devices and tablets are not currently supported.</li>
                <li>Available only on desktop and laptop systems.</li>
                <li>Designed for widescreen layouts; touch devices may not provide full functionality.</li>
                <li>A broadband internet connection (10 Mbps or faster) is recommended for real-time parking updates.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Home page ──────────────────────────────────────
// ─── Home page ──────────────────────────────────────

export default function HomePage() {
  useReveal();

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <HowItWorksSection />
        <ImageShowcaseSection />
        <SystemRequirementsSection />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}

